import { useCallback, useEffect } from 'react';
import { useRouter, useSearchParams } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';

import { useAuthContext } from '../hooks';

type Props = {
  children: React.ReactNode;
};

export default function GuestGuard({ children }: Props) {
  const router = useRouter();
  const searchParams = useSearchParams();

  // Get the returnTo URL parameter and use the full URL if available
  const returnTo = searchParams.get('returnTo') || '/';

  const { authenticated, user } = useAuthContext();

  const check = useCallback(() => {
    if (authenticated) {
      if (!user?.firstname) {
        // If authenticated but no firstname, redirect to profile
        router.replace(paths.auth.profile);
      } else if (user?.id) {
        try {
          // Decode the returnTo URL if it's URL encoded
          const decodedReturnTo = decodeURIComponent(returnTo);
          console.log('Redirecting to returnTo:', decodedReturnTo);

          // Check if it's a full URL and handle it properly
          if (decodedReturnTo.startsWith('http://') || decodedReturnTo.startsWith('https://')) {
            // For full URLs, we might want to extract just the path and query
            // if we don't want to allow cross-domain redirects
            const url = new URL(decodedReturnTo);
            router.replace(url.pathname + url.search);
          } else {
            // For relative URLs, use as is
            router.replace(decodedReturnTo);
          }
        } catch (error) {
          console.error('Error processing returnTo URL:', error);
          router.replace('/');
        }
      }
    }
  }, [authenticated, returnTo, router, user]);

  useEffect(() => {
    check();
  }, [check]);

  return <>{children}</>;
}
